import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// 계획서 요구 2b 선택 A(26-08-21) — 지역표 행 클릭 → 고객사 마스터 이동에 useNavigate.
import { Link, useNavigate } from 'react-router-dom';
import { useState, type CSSProperties } from 'react';
// 개선 9건 차수2d(26-08-04) — 검수 판정 어휘·표기·역할은 shared/permissions 정본.
import {
  BUSINESS_SHEET_VIEW_ROLES,
  CONTRACT_RENEWAL_REVIEW_DECISION_LABEL,
  ContractRenewalReviewDecision,
  UserRole,
} from '@bhops/shared';
import { BusinessRegionsSection } from './BusinessRegionsSection';
import {
  teamDashboardApi,
  type BusinessDashboard,
  type InstallOverview,
} from '@/api/team-dashboard';
import type { IconName } from '@/components/Icon';
// 마감 차수(26-08-24) — '(지역 미지정)' 버킷 글자의 콘솔 사본을 **없앴다.** 서버가 응답에
// 버킷 키를 실어 주고(installOverview.unassignedRegionKey) 이 화면은 그 값을 라벨·링크에
// 그대로 쓴다. 리터럴을 여기 다시 적으면 게이트 없는 미러가 되살아난다.
import {
  dashboardApi,
  type DashboardTrendRange,
  // 개선 9건 차수2c(26-08-04) §6 — 재계약율 응답 타입(분기 버킷).
  type RenewalRateBucket,
} from '@/api/dashboard';
import { TrendLineChart } from '@/components/charts/TrendLineChart';
// 개선 9건 차수2c(26-08-04) §6 — 원장 시작 시점 표기도 KST 정본으로(kstYmd).
import { kstTodayYmd, kstYmd } from '@/lib/format-date';
import { canSeePrice } from '@/permissions';
import { useAuthStore } from '@/stores/auth';
import {
  AutoRefreshHint,
  DashboardError,
  DashboardSkeleton,
  HorizontalBars,
  IconLabel,
  KpiCard,
  MonthlyTrendBars,
  SHARED_EMPTY_HINT,
  SHARED_REFETCH_INTERVAL_MS,
  Section,
} from './_shared';

// round10 B3 — KRW 압축 표기(만/억). 막대 위 라벨/tooltip 용.
function formatKrwCompact(won: number): string {
  if (won <= 0) return '0';
  if (won >= 100_000_000) {
    const eok = won / 100_000_000;
    return `${eok >= 10 ? Math.round(eok) : eok.toFixed(1)}억`;
  }
  if (won >= 10_000) return `${Math.round(won / 10_000).toLocaleString()}만`;
  return Math.round(won).toLocaleString();
}

const REV_CHART_LABEL: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  marginBottom: 6,
  fontWeight: 500,
};

// docs/plan/team-dashboards-and-production-handoff/plan.md §8.3
// S1 Phase 10 (R2 #8) — 사업 대시보드 슬림화.
// 카드 3개만: ① 이번 달 신규 계약 ② 마감 임박 계약 ③ 활성 계약 핸드오프 단계.
// 영업 파이프라인 / 영업 담당자별 실적 제거. 핸드오프 단계는 설치 대기 /
// 설치 완료 / 회수 예정 (사인 대기 제거).

const WINDOW_LABEL: Record<string, string> = {
  D0: '오늘 이내',
  D30: '30일 이내',
  D90: '90일 이내',
};

// 핸드오프 단계 칩 — 라벨 / 색상.
const HANDOFF_STAGES: Array<{
  key: 'installReady' | 'installDone' | 'returnPlanned';
  label: string;
  bg: string;
  fg: string;
}> = [
  // 2026-10-27 오너 "설치 대기랑 완료 색 구분좀 잘 가게" — 대기는 주황, 완료는 초록, 회수 예정은 빨강.
  // 종전 info · success 옅은 바탕은 이 테마에서 둘 다 청록으로 보여 갈리지 않았다.
  {
    key: 'installReady',
    label: '설치 대기',
    bg: 'color-mix(in srgb, #d97706 14%, var(--bg-surface))',
    fg: '#b45309',
  },
  {
    key: 'installDone',
    label: '설치 완료',
    bg: 'color-mix(in srgb, #16a34a 14%, var(--bg-surface))',
    fg: '#15803d',
  },
  {
    key: 'returnPlanned',
    label: '회수 예정',
    bg: 'var(--bg-danger-subtle)',
    fg: 'var(--fg-danger)',
  },
];

// 계약 사실화 1단계(26-07-28) — 서버(dashboard/trends/contracts-churn)에만 있고
// 어느 화면에도 붙어 있지 않던 계약 이탈률 추이를 사업 대시보드에 켠다.
// '만료 임박' 카드(전방)와 한 화면에서 후방(성과)을 같이 본다.
const CHURN_RANGES: DashboardTrendRange[] = ['7d', '30d', '90d'];
const CHURN_RANGE_LABEL: Record<DashboardTrendRange, string> = {
  '7d': '7일',
  '30d': '30일',
  '90d': '90일',
};

function escapeCsvCell(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function BusinessDashboardPage() {
  const q = useQuery({
    queryKey: ['dashboard', 'business'],
    queryFn: () => teamDashboardApi.business(),
    staleTime: 60_000,
    refetchInterval: SHARED_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  // round10 B3 — 월별 계약건수 + 수익(별도 쿼리, 기존 slim payload 무변경).
  const rev = useQuery({
    queryKey: ['dashboard', 'business-revenue'],
    queryFn: () => teamDashboardApi.businessRevenue(12),
    staleTime: 60_000,
    refetchInterval: SHARED_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  const role = useAuthStore((s) => s.user?.role);
  // 서버가 금액을 채웠는지(응답 priceVisible)를 우선 신뢰. 클라 canSeePrice 는 로딩 전 폴백.
  const priceVisible = rev.data?.priceVisible ?? (role ? canSeePrice(role) : false);

  if (q.isLoading) return <DashboardSkeleton kpiCount={3} />;
  if (q.error) return <DashboardError onRetry={q.refetch} />;
  const d = q.data!;

  const totalHandoff =
    d.handoffStages.installReady + d.handoffStages.installDone + d.handoffStages.returnPlanned;

  function handleExportExpiringContracts() {
    const expiring = d?.expiringContracts ?? [];
    if (expiring.length === 0) return;

    const header = ['계약번호', '고객사', '잔여일', '마감 카테고리'];
    const rows = expiring.map((c) => [
      c.humanId,
      c.customerName,
      String(c.daysRemaining),
      WINDOW_LABEL[c.window] ?? c.window,
    ]);
    const csv = [header, ...rows].map((r) => r.map(escapeCsvCell).join(',')).join('\n');

    // BOM for Excel UTF-8 compatibility
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // 추가수정 2차(26-07-27) §2 — 파일명의 '오늘'도 KST 기준. UTC 였을 때
    // 한국 오전 9시 전에 내려받으면 어제 날짜 파일이 만들어졌다.
    a.download = `expiring-contracts-${kstTodayYmd()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // 숫자 카드 줄의 설치 카드 수(국내 · 해외 · 기존고객사) — 줄의 칸 나눔에 쓴다.
  const installCardCount = d.installOverview
    ? 2 + (typeof d.formerCustomerCountDomestic === 'number' ? 1 : 0)
    : 0;

  return (
    // 칸 사이 간격 48px — 2026-10-27 오너 "내가 표시한 부분들 간격좀 띄워줘", "조금 더 넓혀야돼", "조금 더 넓혀보자".
    <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
      {/* 숫자 카드 줄과 설치 현황은 붙여 둔다(사이 24px, 다른 칸 사이는 48px) — 오너 "여기 사이 간격은 좀 줄여도 되겠다". */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* KPI cards — 2026-10-27 오너 "한줄로 정리" — 계약 3장과 설치 3장을 한 줄에.
            핸드오프 카드(셋째)는 단계 칩이 들어가 내용 폭만큼(max-content) 쓰고, 나머지 카드가 남는 폭을
            고르게 나눈다(오너 "이 정도는 잘라내도 될거같은데"). */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(2, minmax(0, 1fr)) max-content repeat(${installCardCount}, minmax(0, 1fr))`,
            gap: 8,
          }}
        >
          <KpiCard
            to="/business/master?sort=contractDate"
            title="이번 달 신규 계약"
            value={d.newContractsThisMonth}
            icon={KPI_ICON.newContracts}
            hint="이번 달에 맺은 계약 수입니다. 누르면 고객사 마스터가 계약일 순으로 열립니다."
          />
          {/* 다계약 표시 차수3 정정3(26-08-06) ㉡ — 이 숫자와 아래 '상세' 목록이 이번 차수에
              **판매 전용 계약을 뺐다**(판매는 소유가 고객사로 넘어가 반납·갱신이라는 사건이
              없다 — 서버 team-dashboard.getBusiness). 숫자가 줄어드는데 화면이 침묵하면
              운영자는 계약이 누락됐다고 읽는다. 그래서 무엇을 세는지 hint 가 말한다.
              ⚠ 이 숫자는 서버 상한(10건)에 걸리면 거기서 멈춘다 — **이번 차수와 무관한
                종전부터의 결함**이고, 고치려면 서버가 총건수를 따로 내려야 한다(응답 필드 추가).
                여기서 '최대 10건'이라고 적지 않는 이유: 그 사실은 이 카드만의 것이 아니라
                목록·CSV 도 함께 걸리는 사실이라 한 카드의 hint 로 말할 자리가 아니다. */}
          <KpiCard
            to="/business/master?renewal=90"
            title="마감 임박 계약"
            value={d.expiringContracts.length}
            icon={KPI_ICON.expiring}
            hint="90일 안에 끝나는 구독 계약 수입니다. 판매는 반납이 없어 세지 않습니다. 누르면 고객사 마스터의 90일 갱신 목록이 열립니다."
            warningLevel="amber"
          />
          {/* 2026-10-27 오너 "활성 계약 핸드오프를 방금 올린 저거로 치환하고, 크기 조정을 하자" — 숫자 카드
              자리에 단계 칩(설치 대기 · 설치 완료 · 회수 예정)을 넣고 두 칸 폭을 쓴다. */}
          <HandoffCard stages={d.handoffStages} total={totalHandoff} />
          {d.installOverview && (
            <InstallKpiCards
              overview={d.installOverview}
              formerDomestic={d.formerCustomerCountDomestic}
              formerOverseas={d.formerCustomerCountOverseas}
            />
          )}
        </div>

        {/* 계획서 요구 2b(26-08-21) — 설치 현황(국내/해외/기존고객사 카드 + 지역표).
            자리는 KPI 카드 줄 **바로 아래**(화면 규칙 — 스크롤 없이 보인다).
            구버전 서버는 이 칸을 내리지 않는다 → 섹션을 그리지 않고 그 사실을 한 줄로
            말한다(조용한 빈칸 금지 — 마스터의 회수일 안내와 같은 관례). */}
        {d.installOverview === undefined ? (
          <div role="status" data-testid="install-overview-missing" style={SHARED_EMPTY_HINT}>
            이 서버가 아직 설치 현황을 보내지 않습니다. 서버가 새 버전으로 바뀌면 국내와 해외 설치
            대수, 지역표, 기존고객사 수가 여기에 나타납니다.
          </div>
        ) : (
          <InstallOverviewSection overview={d.installOverview} />
        )}
      </div>

      {/* 2026-10-26 — 권역별 현황(엑셀 '권역별현황'). 시트 데이터를 쓰므로 시트 화면과 같은 역할만. */}
      {role && BUSINESS_SHEET_VIEW_ROLES.includes(role) && <BusinessRegionsSection />}

      {/* round10 B3 — 월별 계약건수 + 구독/판매 수익. 건수는 전역, 금액은 price-visible 만. */}
      <Section title="최근 12개월 월별 계약과 수익">
        {rev.isLoading ? (
          <div style={SHARED_EMPTY_HINT}>불러오는 중…</div>
        ) : rev.error || !rev.data ? (
          <div style={{ color: 'var(--fg-danger)', fontSize: 12 }}>
            수익 데이터를 불러오지 못했습니다.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 16,
              }}
            >
              <div>
                <div style={REV_CHART_LABEL}>월별 활성 계약 건수</div>
                <MonthlyTrendBars
                  points={rev.data.months.map((m) => ({ ym: m.ym, count: m.contractCount }))}
                  color="var(--brand)"
                  emptyText="계약 데이터가 없습니다."
                  formatValue={(n) => `${n}건`}
                />
              </div>
              {priceVisible ? (
                <>
                  <div>
                    <div style={REV_CHART_LABEL}>월 구독 수익 (MRR)</div>
                    <MonthlyTrendBars
                      points={rev.data.months.map((m) => ({
                        ym: m.ym,
                        count: m.subscriptionMonthly,
                      }))}
                      color="var(--robot-operating)"
                      emptyText="구독 수익이 없습니다."
                      formatValue={formatKrwCompact}
                    />
                  </div>
                  <div>
                    <div style={REV_CHART_LABEL}>월 판매 수익</div>
                    <MonthlyTrendBars
                      points={rev.data.months.map((m) => ({ ym: m.ym, count: m.saleOneTime }))}
                      color="var(--robot-shipping)"
                      emptyText="판매 수익이 없습니다."
                      formatValue={formatKrwCompact}
                    />
                  </div>
                </>
              ) : (
                <div style={SHARED_EMPTY_HINT}>
                  수익 금액은 권한이 있는 사용자에게만 표시됩니다.
                </div>
              )}
            </div>
            {priceVisible && rev.data.missingPriceCount > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--dday-warning)' }}>
                금액이 입력되지 않은 상용 계약이 {rev.data.missingPriceCount}건 있습니다. 실제
                수익은 이보다 높을 수 있습니다.
              </div>
            )}
          </>
        )}
      </Section>

      {/* 계약 사실화 1단계(26-07-28) — 계약 이탈률 추이(비율 지표라 금액 권한 무관).
          개선 9건 차수2c(26-08-04) §6 — 재계약율을 **그 옆에** 둔다(계획서 §6).
          이탈(잃은 것)과 재계약(지킨 것)은 같은 질문의 앞뒤라 한눈에 같이 봐야 한다.
          좁은 창에서는 자동으로 위아래로 쌓인다(minmax + auto-fit). */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: 16,
        }}
      >
        <ContractChurnSection />
        <RenewalRateSection />
      </div>

      {/* 개선 9건 차수2d(26-08-04) — '확인 필요' 갱신. 재계약율 **바로 아래**에 둔다.
          이 목록을 처리하면 위 숫자가 움직이므로, 숫자와 그 숫자를 바꾸는 행동이 붙어
          있어야 한다. 처리할 것이 없으면 카드 자체가 나타나지 않는다. */}
      {/* 2026-10-27 오너 "둘이 같은 라인에 있게 하자" — 마감 임박 계약과 확인이 필요한 갱신 기록을
          한 줄에 나란히. 한쪽이 없으면 남은 칸이 줄을 다 쓴다. */}
      <div style={pairRow}>
        {/* 마감 임박 계약 — detail list */}
        {d.expiringContracts.length > 0 && (
          <Section
            style={pairCard}
            title="마감 임박 계약 상세"
            extra={
              <button
                type="button"
                onClick={handleExportExpiringContracts}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  border: '1px solid var(--border-default)',
                  background: 'var(--bg-surface)',
                  borderRadius: 'var(--r-btn)',
                  cursor: 'pointer',
                }}
              >
                CSV 내보내기
              </button>
            }
          >
            {/* 2026-10-27 오너 "표 헤더가 이상하게 잘리네" — 칸 사이 틈(기본 border-spacing)을
              없애고 칸마다 안쪽 여백을 줘서 머리글이 한 줄 띠로 이어지게 한다. */}
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={expiringCell}>계약</th>
                  <th style={expiringCell}>고객사</th>
                  <th style={{ ...expiringCell, width: 90 }}>남은 날</th>
                  <th style={{ ...expiringCell, width: 110 }}>마감</th>
                  <th style={{ ...expiringCell, width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {d.expiringContracts.map((c) => (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td
                      style={{
                        ...expiringCell,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                      }}
                    >
                      {c.humanId}
                    </td>
                    <td style={expiringCell}>{c.customerName}</td>
                    <td style={{ ...expiringCell, textAlign: 'right' }}>{c.daysRemaining}일</td>
                    <td
                      style={{
                        ...expiringCell,
                        textAlign: 'right',
                        color:
                          c.window === 'D0'
                            ? 'var(--fg-danger)'
                            : c.window === 'D30'
                              ? 'var(--dday-warning)'
                              : 'var(--fg-muted)',
                        fontWeight: c.window === 'D0' ? 600 : 400,
                      }}
                    >
                      {WINDOW_LABEL[c.window] ?? c.window}
                    </td>
                    <td style={{ ...expiringCell, textAlign: 'right' }}>
                      {/* FB2 §3A — 계약 상세(redirect 경유) 대신 고객사 상세 직링크. */}
                      <Link to={`/customers/${c.customerId}`} style={linkSmall}>
                        상세 →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
        <RenewalReviewSection />
      </div>

      <AutoRefreshHint isFetching={q.isFetching} />
    </div>
  );
}

const linkSmall: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};

// ============================================================
// 계획서 요구 2b(26-08-21) — 설치 현황: KPI 카드 3개 + 지역별 설치 카드
// 9/2 오너 요청 — 전폭 3열 표 하나였던 지역표를 카드 셋(국내 목록·그래프·해외)으로.
// ============================================================
//
// 화면 규칙(구속) 그대로:
//   · KPI 카드: '국내 설치 N대'·'해외 설치 N대'(셈법 한 줄) + 선택 B '기존고객사 N곳'.
//   · 지역 목록: 설치 대수 내림차순, '(지역 미지정)'·'(미상)' 버킷은 맨 아래 고정,
//     머리에 두 셈의 기준 한 줄, 전체 0이면 목록 대신 한 줄 안내. 기존 카드 스타일 재사용.
//   · 선택 A: 지역 행 클릭 → 고객사 마스터가 그 지역으로 걸러진 채 열림.
//     해외 국가 행은 해외 탭 + 국가 필터. '(지역 미지정)' 행은 미지정만.
//     '(미상)' 행은 **클릭 없음** — 고객사 연결이 없는 배치라 마스터에 대응하는
//     행 자체가 없다(없는 목적지로 보내는 것보다 이유를 말하는 쪽이 정직하다).

const expiringCell: CSSProperties = { padding: '7px 10px' };

/** 두 설치 카드의 셈법 한 줄 — 서버 where(정본 변형)가 실제로 세는 것과 같은 문장. */
const INSTALL_COUNT_RULE =
  '고객 매장에 설치된 대수입니다. 행사, 시연, 스페어는 빼고 회수 예정은 넣었습니다.';
/**
 * 지역 카드 머리의 기준 한 줄 — 대수(매장 기준)와 고객사 수(본사 기준)는 **다른 셈**이다.
 * 이 줄이 없으면 "서울 본사가 부산 매장에 로봇을 둔" 행(고객사 0 · 로봇 N)이 결함으로 보인다.
 */
const REGION_TABLE_BASIS_TEXT =
  '설치 대수는 로봇이 있는 매장을, 고객사 수는 본사 주소를 기준으로 셉니다.';
/** 기존고객사 카드의 뜻 한 줄 — 서버 판정(isFormerCustomer)의 세 조건을 일상어로. */
const FORMER_CUSTOMER_RULE = '계약이 끝나고 로봇이 모두 회수된 곳';

/**
 * 설치 카드 3장(국내 · 해외 · 기존고객사). 2026-10-27 오너 "한줄로 정리 가능할거같은데" — 맨 위
 * 숫자 카드 줄에 함께 서도록 InstallOverviewSection 에서 떼어 냈다.
 */
/** 숫자 카드 이름 앞 색 아이콘(2026-10-27 오너 "나머지 것들도 색이랑 테마를 조정해"). */
const KPI_ICON = {
  newContracts: { name: 'file', color: '#2f6fe0' },
  expiring: { name: 'clock', color: '#d97706' },
  handoff: { name: 'truck', color: '#0891b2' },
  domestic: { name: 'map-pin', color: '#16a34a' },
  overseas: { name: 'globe', color: '#7c4dff' },
  former: { name: 'history', color: '#db2777' },
} satisfies Record<string, { name: IconName; color: string }>;

/** 활성 계약 핸드오프 — 합계와 단계 칩. 숫자 카드 줄에서 두 칸 폭. */
function HandoffCard({
  stages,
  total,
}: {
  stages: BusinessDashboard['handoffStages'];
  total: number;
}) {
  return (
    <Link
      to="/business/master"
      title="설치 대기, 설치 완료, 회수 예정 계약입니다. 누르면 고객사 마스터가 열립니다."
      style={handoffCard}
    >
      <IconLabel icon={KPI_ICON.handoff.name} color={KPI_ICON.handoff.color}>
        활성 계약 핸드오프
      </IconLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>{total}</span>
        {HANDOFF_STAGES.map((s) => (
          <span
            key={s.key}
            style={{
              fontSize: 12,
              padding: '2px 10px',
              borderRadius: 999,
              background: s.bg,
              color: s.fg,
              fontWeight: 500,
            }}
          >
            {s.label}: {stages[s.key]}
          </span>
        ))}
      </div>
    </Link>
  );
}

const handoffCard: CSSProperties = {
  display: 'block',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--border-card)',
  background: 'var(--bg-surface)',
  textDecoration: 'none',
  color: 'inherit',
};

function InstallKpiCards({
  overview,
  formerDomestic,
  formerOverseas,
}: {
  overview: InstallOverview;
  formerDomestic: number | undefined;
  formerOverseas: number | undefined;
}) {
  return (
    <>
      <KpiCard
        to="/business/master"
        title="국내 설치"
        value={`${overview.domesticCount}대`}
        icon={KPI_ICON.domestic}
        hint={`${INSTALL_COUNT_RULE} 누르면 고객사 마스터의 국내 목록이 열립니다.`}
      />
      <KpiCard
        to="/business/master/overseas"
        title="해외 설치"
        value={`${overview.overseasCount}대`}
        icon={KPI_ICON.overseas}
        hint={`${INSTALL_COUNT_RULE} 누르면 고객사 마스터의 해외 목록이 열립니다.`}
      />
      {/* 선택 B — 기존고객사. 서버가 이 칸만 안 내리는 조합은 없지만(같은 배포 차수)
              타입이 옵셔널이므로 지어내지 않고 카드를 생략한다.
              마감 차수(26-08-24) — 큰 숫자는 **국내**다. 목적지 `?former=1` 은 라우터가
              scope="domestic" 을 주입하는 국내 목록이라, 국가를 안 가린 수를 크게 적으면
              "카드 7 vs 목록 6" 이 벌어진다(설치 카드가 hint 에 "(국내)"를 적는 그 관례로
              목적지를 밝힌다). 해외는 0 이 아닐 때만 부제로 덧붙이고, 그 부제는 갈 곳이
              있다 — 해외 탭 + former 필터. 0 이면 아무 것도 안 붙는다(소음 없음). */}
      {typeof formerDomestic === 'number' && (
        <KpiCard
          to="/business/master?former=1"
          title="기존고객사"
          value={`${formerDomestic}곳`}
          icon={KPI_ICON.former}
          hint={`${FORMER_CUSTOMER_RULE}입니다. 누르면 고객사 마스터의 국내 목록이 기존고객사만 걸러 열립니다.`}
          sub={
            formerOverseas
              ? {
                  to: '/business/master/overseas?former=1',
                  label: `해외 ${formerOverseas}곳 →`,
                  title: '누르면 고객사 마스터의 해외 탭이 기존고객사만 걸러 열립니다.',
                }
              : undefined
          }
        />
      )}
    </>
  );
}

function InstallOverviewSection({ overview }: { overview: InstallOverview }) {
  const navigate = useNavigate();

  // 정렬(대수 내림차순 → 고객사 수 → 키)은 서버가 이미 해서 내리지만, '내림차순'은 화면
  // 규칙이기도 하므로 같은 비교로 한 번 더 접는다 — 서버 정렬이 바뀌어도 표가 규칙을 지키고,
  // 그 사실을 시험이 픽스처를 일부러 뒤섞어 확인한다.
  const regions = [...overview.regions].sort(
    (a, b) => b.robots - a.robots || b.customers - a.customers || a.key.localeCompare(b.key, 'ko'),
  );
  // 9/2 오너 요청 — 국내 시·도와 해외 국가를 갈라 카드를 따로 준다. 서버가 이미 행마다
  // overseas 를 내리므로 서버 변경은 없다(api/team-dashboard InstallRegionRow).
  const domestic = regions.filter((r) => !r.overseas);
  const overseas = regions.filter((r) => r.overseas);
  // 버킷 행은 값이 0 이면 그리지 않는다(계약: 0이면 행 생략). '(지역 미지정)'은 로봇이
  // 없어도 본사 주소 빈 고객사가 있으면 남긴다 — 지우면 그 고객사가 표 어디에도 없다.
  const showUnassigned =
    overview.unassignedRegionRobots > 0 || overview.unassignedRegionCustomers > 0;
  // 마감 차수(26-08-24) — 버킷 라벨·링크에 쓸 **서버가 준 글자**. 없으면(구버전) 지어내지
  // 않는다(아래 버킷 행 주석).
  const unassignedKey = overview.unassignedRegionKey;
  const showUnknown = overview.unknownCustomerRobots > 0;
  const allZero = regions.length === 0 && !showUnassigned && !showUnknown;

  // 그래프는 처음엔 상위 몇 곳만 — 17개 시·도를 전부 그리면 카드가 옆 표보다 훨씬 길어진다.
  // 2026-10-27 오너 요청: 아래 '더보기'를 누르면 나머지 지역도 막대로 펼친다.
  const [showAllBars, setShowAllBars] = useState(false);
  const restRegions = domestic.slice(GRAPH_BAR_MAX);
  const restRobots = restRegions.reduce((sum, r) => sum + r.robots, 0);
  const barRegions = showAllBars ? domestic : domestic.slice(0, GRAPH_BAR_MAX);
  // 지역이 정해지지 않은 대수는 막대가 될 수 없다 — 그래프에 없는 이유를 적는다.
  const offBarParts = [
    overview.unassignedRegionRobots > 0
      ? `${unassignedKey ?? '(지역 이름을 받지 못함)'} ${overview.unassignedRegionRobots}대`
      : null,
    overview.unknownCustomerRobots > 0 ? `(미상) ${overview.unknownCustomerRobots}대` : null,
  ].filter((v): v is string => v !== null);

  return (
    <>
      {allZero ? (
        <Section title="국내 설치 현황">
          <div style={SHARED_EMPTY_HINT} data-testid="install-regions-empty">
            고객 매장에 나가 있는 로봇이 없습니다. 설치가 생기면 지역표가 나타납니다.
          </div>
        </Section>
      ) : (
        <>
          {/* 2026-10-27 오너 요청 — 국내 설치 현황 · 해외 설치 현황 · 지역별 설치 그래프 세 칸을
              한 줄에. 칸 폭은 내용 양에 맞춰 국내 > 그래프 > 해외 순으로 나눈다. */}
          <div style={installCardRow}>
            <Section title="국내 설치 현황" style={installTableCard}>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 6 }}>
                {REGION_TABLE_BASIS_TEXT}
              </div>
              {domestic.length === 0 ? (
                <div style={SHARED_EMPTY_HINT}>국내 설치가 없습니다.</div>
              ) : (
                // 2026-10-27 오너 요청 — 한눈에 보이게. '69대 · 37곳'처럼 한 덩어리로 붙어 있던
                // 숫자를 열로 갈라 머리를 달고, 줄 사이에 가로줄을 긋는다. 많으면 두 표로
                // 나란히 두되 열 우선(왼쪽 표가 1~N위)이라 순위가 지그재그가 되지 않는다.
                // 지역 미지정 · 미상 버킷은 항상 맨 끝(오른쪽 표의 아래)에 흐린 글자로 붙는다.
                <RegionTable
                  rows={[
                    ...domestic.map(
                      (r): RegionRowData => ({
                        key: `kr-${r.key}`,
                        label: r.key,
                        robots: r.robots,
                        customersText: `${r.customers}곳`,
                        title: `누르면 고객사 마스터가 '${r.key}' 지역만 걸러 열립니다.`,
                        onClick: () =>
                          navigate(`/business/master?region=${encodeURIComponent(r.key)}`),
                      }),
                    ),
                    // 버킷 행의 글자는 **서버가 준 키**다(사본 없음). 키를 안 내리는 서버에서는
                    // 수는 그대로 보여 주고 라벨 자리에 그 사실을 적으며 링크를 걸지 않는다.
                    ...(showUnassigned
                      ? [
                          {
                            key: 'bucket-unassigned',
                            label: unassignedKey ?? '(지역 이름을 받지 못함)',
                            robots: overview.unassignedRegionRobots,
                            customersText: `${overview.unassignedRegionCustomers}곳`,
                            muted: true,
                            title:
                              unassignedKey === undefined
                                ? '지역이 정해지지 않은 묶음의 이름을 서버에서 받지 못해 목록으로 이동할 수 없습니다. 대수와 고객사 수는 맞습니다.'
                                : '누르면 고객사 마스터에서 본사 주소가 비어 있는 곳만 보여 줍니다.',
                            onClick:
                              unassignedKey === undefined
                                ? undefined
                                : () =>
                                    navigate(
                                      `/business/master?region=${encodeURIComponent(unassignedKey)}`,
                                    ),
                          } satisfies RegionRowData,
                        ]
                      : []),
                    // '(미상)'은 고객사 연결이 없어 셀 수 없다 — 0 이 아니라 '–'.
                    ...(showUnknown
                      ? [
                          {
                            key: 'bucket-unknown',
                            label: '(미상)',
                            robots: overview.unknownCustomerRobots,
                            customersText: '–',
                            muted: true,
                            title:
                              '고객사와 연결되지 않은 배치라 고객사 마스터에 해당하는 줄이 없습니다. 이 대수는 국내와 해외 카드 어디에도 넣지 않았습니다.',
                          } satisfies RegionRowData,
                        ]
                      : []),
                  ]}
                />
              )}
            </Section>

            {/* 해외는 지역이 아니라 **국가**다 — 같은 축에 섞으면 순위가 거짓말이 된다.
                0 이면 카드 자체를 그리지 않는다(소음 없음). 국가는 마스터 해외 탭과 같은
                어휘인 국가 코드 그대로 — 한글 국가명 표는 그 화면과 함께 갈아야 한다. */}
            {overseas.length > 0 && (
              <Section title="해외 설치 현황" style={installTableCard}>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 6 }}>
                  국가 코드로 나눴습니다. {REGION_TABLE_BASIS_TEXT}
                </div>
                <RegionTable
                  wide
                  rows={overseas.map((r) => ({
                    key: `os-${r.key}`,
                    label: r.key,
                    robots: r.robots,
                    customersText: `${r.customers}곳`,
                    title: `누르면 고객사 마스터의 해외 탭이 '${r.key}' 국가만 걸러 열립니다.`,
                    onClick: () =>
                      navigate(`/business/master/overseas?country=${encodeURIComponent(r.key)}`),
                  }))}
                />
              </Section>
            )}

            {/* 그래프 카드 — "어디가 많은가"를 자릿수를 읽지 않고 보게 한다. 대시보드가
                이미 쓰는 가로 막대 부품 재사용(제조 '로봇 상태 분포'와 같은 생김새) —
                차트 라이브러리를 새로 끌어오지 않는다.
                국내가 0이면 카드를 아예 그리지 않는다 — 옆 카드가 이미 같은 문장을 말한다. */}
            {domestic.length > 0 && (
              <Section title="지역별 설치 그래프" style={{ flex: '1 1 0', minWidth: 0 }}>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 6 }}>
                  막대를 누르면 그 지역의 고객사 목록이 열립니다.
                </div>
                <HorizontalBars
                  items={barRegions.map((r) => ({
                    key: r.key,
                    label: r.key,
                    count: r.robots,
                    title: `${r.key}: ${r.robots}대, 고객사 ${r.customers}곳`,
                    onClick: () => navigate(`/business/master?region=${encodeURIComponent(r.key)}`),
                  }))}
                />
                {restRegions.length > 0 && (
                  <button
                    type="button"
                    data-testid="install-bars-more"
                    onClick={() => setShowAllBars((v) => !v)}
                    style={moreButton}
                  >
                    {showAllBars
                      ? '접기'
                      : `나머지 ${restRegions.length}개 지역 ${restRobots}대 더 보기`}
                  </button>
                )}
                {/* 막대에 없는 대수를 침묵하지 않는다. 고객사만 있고 로봇이 0인 버킷은 적지 않는다. */}
                {offBarParts.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 8 }}>
                    {`${offBarParts.join(', ')}는 지역이 정해지지 않아 그래프에는 없고 표에만 있습니다.`}
                  </div>
                )}
              </Section>
            )}
          </div>
        </>
      )}
    </>
  );
}

interface RegionRowData {
  key: string;
  label: string;
  robots: number;
  customersText: string;
  title?: string;
  onClick?: () => void;
  muted?: boolean;
}

/**
 * 지역 표 — 지역 · 설치 대수 · 고객사 세 열. 항목이 많으면 두 표로 나눠 나란히 둔다
 * (열 우선: 왼쪽 표가 위 순위). 탭 정지점은 만들지 않는다 — 지역이 17개면 대시보드
 * 첫머리에 정지점이 17개 생긴다. 상위 지역은 그래프 막대로 키보드 이동이 된다.
 */
function RegionTable({ rows, wide }: { rows: RegionRowData[]; wide?: boolean }) {
  // 해외 표는 줄이 적어 한 표로만 서므로 칸 사이를 넓게 둔다(2026-10-27 오너 "간격을 더 늘려").
  const rowGrid = wide ? { ...regionGridRow, ...regionGridRowWide } : regionGridRow;
  const columns =
    rows.length <= INSTALL_SINGLE_COLUMN_MAX
      ? [rows]
      : [rows.slice(0, Math.ceil(rows.length / 2)), rows.slice(Math.ceil(rows.length / 2))];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns.length}, max-content)`,
        columnGap: 32,
        alignItems: 'start',
      }}
    >
      {columns.map((col, ci) => (
        <div key={ci}>
          <div style={{ ...rowGrid, ...regionHead }}>
            <span>지역</span>
            <span style={{ textAlign: 'right' }}>대수</span>
            <span style={{ textAlign: 'right' }}>고객사</span>
          </div>
          {col.map((r) => (
            <div
              key={r.key}
              data-testid="install-region-row"
              onClick={r.onClick}
              title={r.title}
              style={{
                ...rowGrid,
                ...regionBodyRow,
                cursor: r.onClick ? 'pointer' : 'default',
                color: r.muted ? 'var(--fg-muted)' : undefined,
              }}
            >
              <span style={installRegionName}>{r.label}</span>
              <span style={{ ...regionNum, fontWeight: r.muted ? 400 : 700 }}>{r.robots}대</span>
              <span style={{ ...regionNum, color: 'var(--fg-muted)' }}>{r.customersText}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** 이 수 이하이면 굳이 두 표로 쪼개지 않는다(한 표가 반쯤 비면 더 못생긴다). */
const INSTALL_SINGLE_COLUMN_MAX = 6;

/** 그래프 카드에 처음 그릴 막대 수 — 옆 표(두 표 8~9줄)와 높이가 맞는 수. 나머지는 '더보기'. */
const GRAPH_BAR_MAX = 8;

// 표 칸은 표 폭만큼만 차지한다(설명 문장은 그 폭에서 줄바꿈). 남는 폭은 그래프 칸이 가져간다.
const installTableCard: CSSProperties = { flex: '0 0 auto', width: 'min-content' };

const installCardRow: CSSProperties = {
  display: 'flex',
  gap: 16,
  alignItems: 'stretch',
};

const regionGridRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '44px 36px 36px',
  alignItems: 'center',
  gap: 6,
  padding: '0 4px',
};

const regionGridRowWide: CSSProperties = {
  gridTemplateColumns: '64px 56px 56px',
  gap: 12,
};

const regionHead: CSSProperties = {
  height: 26,
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  borderBottom: '1px solid var(--border-strong)',
};

// 2026-10-27 오너 "색으로 구분하지마", "열 구분 선은 없는 표로" — 줄마다 칠하던 바탕 대신 가로줄만.
const regionBodyRow: CSSProperties = {
  height: 26,
  fontSize: 13,
  borderBottom: '1px solid var(--border-subtle)',
};

const regionNum: CSSProperties = {
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const moreButton: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 8,
  padding: '6px 0',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const installRegionName: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// 계약 사실화 1단계(26-07-28) — 계약 이탈률 추이 섹션.
// 금액이 아니라 비율이므로 priceVisible(금액 권한) 게이팅을 걸지 않는다 —
// 이 화면의 금액 차트만 권한으로 가리고, 비율 지표는 모든 역할이 같이 본다.
function ContractChurnSection() {
  // 기본 30일. 7일은 표본이 적어 계단 모양이 되고 90일은 추세 확인용 —
  // 서버가 이미 3창을 지원하므로 버튼만 노출한다(집계 로직 무변경).
  const [range, setRange] = useState<DashboardTrendRange>('30d');

  // TrendLineChart 와 동일한 queryKey → 요청은 1건(react-query 캐시 공유).
  // 여기서는 '전 구간 0%' 판정(빈 상태 문구)에만 쓴다.
  const churn = useQuery({
    queryKey: ['dashboard', 'trends', 'contracts-churn', range],
    queryFn: () => dashboardApi.trend('contracts-churn', range),
    staleTime: 60_000,
  });
  const buckets = churn.data?.buckets ?? [];
  // 서버는 모수(당일 활성 계약)가 0인 날도 0 으로 내려보낸다 — 날짜별로는
  // '이탈 0%'와 '모수 없음'을 구분할 수 없으므로 각주로 밝힌다.
  //
  // 계약 사실화 1단계 정정(26-07-28) — 이 값으로 "종료된 계약이 없습니다" 라고
  // 단정하면 **사실과 반대되는 문장**을 사업 대시보드에 띄울 수 있다. 서버
  // 셈법(dashboard/trends computeChurn)은 모수가 0이면 분자를 보지 않고 0 을
  // 내려보내고, 모수인 '당일 활성 계약'은 status=ACTIVE 인 계약만 세므로 그날
  // 종료된 계약은 이미 모수에서 빠져 있다. 즉 계약이 실제로 종료된 날도
  // 0% 로 올 수 있다(1대 고객사가 해지하면 그날 분모=0 → 0%).
  // 그래서 문구는 판정을 단정하지 않고 두 경우를 함께 적는다.
  const allZero = buckets.length > 0 && buckets.every((b) => b.value === 0);

  return (
    <Section
      title="계약 이탈률 추이"
      extra={
        <div role="group" aria-label="이탈률 기간 선택" style={{ display: 'flex', gap: 4 }}>
          {CHURN_RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              aria-pressed={range === r}
              style={{ ...churnRangeButton, ...(range === r ? churnRangeButtonActive : null) }}
            >
              {CHURN_RANGE_LABEL[r]}
            </button>
          ))}
        </div>
      }
    >
      <TrendLineChart
        metric="contracts-churn"
        range={range}
        title={`최근 ${CHURN_RANGE_LABEL[range]} 이탈률`}
        asPercent
        color="var(--dday-warning)"
      />
      {allZero && (
        <div style={SHARED_EMPTY_HINT} data-testid="churn-all-zero-hint">
          모든 날이 0%입니다. 끝난 계약이 없거나, 나눌 기준인 그날의 활성 계약이 없었습니다.
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-muted)' }}>
        해지나 만료로 끝난 계약 수를 그날의 활성 계약 수로 나눈 값이며, 한국 시간 하루 단위로
        셉니다. 활성 계약이 없던 날은 0%로 표시됩니다. 그날 끝난 계약이 있어도 계산에서 빠지므로
        0%를 &apos;종료 없음&apos;으로 읽지 마세요.
      </div>
    </Section>
  );
}

// ============================================================
// 개선 9건 차수2c(26-08-04) §6 — 재계약율 카드 + 분기 추이
// ============================================================
//
// 이 화면이 반드시 지켜야 하는 두 가지(서버 응답 주석과 같은 규칙):
//   ① rate === null 은 **0% 가 아니다** — 모수 0(아직 만기 도래 계약이 없음)이다.
//      0 으로 접어 그리면 "재계약율 0%"라는 거짓을 사업 대시보드가 말한다.
//      이탈률 카드의 각주 패턴을 그대로 재사용한다.
//   ② 유예기간(90일)이 안 지난 구간은 '확정 전' — 숫자가 더 올라갈 수 있다.
// 그리고 이 지표는 **원장이 쌓이기 시작한 시점부터** 진실이다(역산은 범위 밖) —
// 화면이 그 사실을 각주로 말한다. 숫자만 보여 주고 침묵하지 않는다.
function formatRatePct(rate: number | null): string {
  return rate === null ? '–' : `${(rate * 100).toFixed(1)}%`;
}

function RenewalRateSection() {
  const q = useQuery({
    queryKey: ['dashboard', 'renewal-rate'],
    queryFn: () => dashboardApi.renewalRate(),
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <Section title="재계약율">
        <div style={SHARED_EMPTY_HINT}>불러오는 중…</div>
      </Section>
    );
  }
  if (q.error || !q.data) {
    return (
      <Section title="재계약율">
        <div style={{ color: 'var(--fg-danger)', fontSize: 12 }}>
          재계약율을 불러오지 못했습니다.
        </div>
      </Section>
    );
  }

  const d = q.data;
  const w = d.window;
  // 모수 0 은 '아직 없음'이다 — 이탈률 카드와 같은 문장 패턴.
  const noDenominator = w.dueRobots === 0;
  const ledgerText = d.ledgerSince
    ? `갱신 기록은 ${kstYmd(d.ledgerSince)}부터 쌓입니다. 그 전의 갱신은 기록이 없어 재계약율이 실제보다 낮게 보일 수 있습니다.`
    : '아직 갱신 기록이 한 건도 없습니다. 이 지표는 지금부터 쌓입니다.';

  return (
    <Section title="최근 12개월 재계약율">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
        <span
          data-testid="renewal-rate-value"
          style={{ fontSize: 30, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatRatePct(w.rate)}
        </span>
        {w.provisional && (
          <span data-testid="renewal-rate-provisional" style={provisionalBadge}>
            확정 전
          </span>
        )}
      </div>

      {noDenominator ? (
        <div style={SHARED_EMPTY_HINT} data-testid="renewal-rate-no-denominator">
          아직 만기 도래 계약이 없습니다.
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
          만기 도래 {w.dueRobots}대 중 {w.renewedRobots}대 갱신, 유예 {d.graceDays}일 기준
          <br />
          고객사 기준 {formatRatePct(w.customerRate)} ({w.dueCustomers}곳 중 {w.renewedCustomers}곳)
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div style={REV_CHART_LABEL}>분기별 재계약율</div>
        <RenewalRateQuarterBars quarters={d.quarters} />
      </div>

      {w.provisionalDueRobots > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--dday-warning)' }}>
          최근 {d.graceDays}일 안에 만기가 도래한 {w.provisionalDueRobots}대는 아직 갱신할 시간이
          남아 있습니다. 이 숫자는 더 올라갈 수 있습니다.
        </div>
      )}
      {w.excludedNoSnapshotEvents > 0 && (
        <div
          style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-muted)' }}
          data-testid="renewal-rate-excluded-hint"
        >
          당시 대수를 알 수 없는 갱신 기록 {w.excludedNoSnapshotEvents}건은 대수 집계에서
          제외했습니다. 지난 기록을 거슬러 채웠거나 손으로 보충한 기록입니다. 고객사 기준에는
          포함됩니다.
        </div>
      )}
      {/* 차수2d — 확인 필요 행은 집계에 **포함**돼 있다. 확정하면 숫자가 움직일 수 있으므로
          그 사실을 말한다(아래 '확인이 필요한 갱신 기록' 카드에서 처리한다). */}
      {w.pendingReviewEvents > 0 && (
        <div
          style={{ marginTop: 6, fontSize: 11, color: 'var(--dday-warning)' }}
          data-testid="renewal-rate-pending-review-hint"
        >
          갱신인지 날짜 정정인지 확인이 필요한 기록 {w.pendingReviewEvents}건이 이 숫자에 포함돼
          있습니다. 아래에서 확정하면 숫자가 바뀔 수 있습니다.
        </div>
      )}
      {w.excludedCorrectionEvents > 0 && (
        <div
          style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-muted)' }}
          data-testid="renewal-rate-correction-hint"
        >
          '날짜 정정'으로 확정된 기록 {w.excludedCorrectionEvents}건은 갱신에서 제외했습니다.
        </div>
      )}
      <div
        style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-muted)' }}
        data-testid="renewal-rate-ledger-hint"
      >
        {ledgerText} 판매 계약은 갱신 개념이 없어 세지 않습니다.
      </div>
    </Section>
  );
}

// ============================================================
// 개선 9건 차수2d(26-08-04) — '확인 필요' 갱신 목록(사람이 닫는 자리)
// ============================================================
//
// 원장은 "갱신인지 날짜 정정인지 확신할 수 없는" 반납일 변경도 지우지 않고 남기고,
// 표시를 붙여 이 목록으로 올린다. 종전에는 그 표시를 **읽는 화면이 없어** 사람이
// 걷어낼 길이 없었다 — 재계약율은 정정을 영구히 갱신으로 셌고, 화면은 그런 일이
// 있는지조차 말하지 않았다. 이 카드가 그 길이다.
//
// 화면이 지키는 것:
//   · 처리할 것이 없으면 **카드를 그리지 않는다**(빈 카드는 소음이다).
//   · 사유를 서버 문장 그대로 보여 준다 — 왜 애매한지 모르면 확정할 수 없다.
//   · 상한(50건)에 걸리면 그 사실을 말한다. 조용히 자르지 않는다.
//   · 확정 권한이 없는 역할에게는 버튼을 **보여 주지 않고**, 왜 안 보이는지 적는다.
//     (막는 것은 서버다. 화면은 헛클릭을 줄이는 것뿐이다.)
function RenewalReviewSection() {
  const role = useAuthStore((s) => s.user?.role);
  const canDecide = canDecideRenewalReview(role);
  const qc = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['dashboard', 'renewal-review'],
    queryFn: () => dashboardApi.renewalReviewList(),
    staleTime: 60_000,
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: ContractRenewalReviewDecision }) =>
      dashboardApi.decideRenewalReview(id, decision),
    onMutate: ({ id }) => {
      setPendingId(id);
      setFailed(null);
    },
    onSettled: () => setPendingId(null),
    onError: () => setFailed('확정하지 못했습니다. 권한이 없거나 이미 처리된 기록일 수 있습니다.'),
    onSuccess: () => {
      // 이 판정이 재계약율을 움직인다. 목록만 새로 받고 위 카드가 낡은 숫자를 그대로
      // 들고 있으면 "확정했는데 아무 일도 없었다"로 보인다 — 둘을 함께 무효화한다.
      void qc.invalidateQueries({ queryKey: ['dashboard', 'renewal-review'] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'renewal-rate'] });
    },
  });

  // 불러오는 중·실패·0건은 카드를 그리지 않는다 — 이 카드는 '할 일이 있을 때만' 뜬다.
  if (!q.data || q.data.rows.length === 0) return null;
  const { rows, total, limit } = q.data;

  return (
    <Section title={`확인이 필요한 갱신 기록 ${total}건`} style={pairCard}>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 8 }}>
        반납일이 바뀐 기록 중, 갱신인지 날짜 정정인지 시스템이 확신할 수 없는 것들입니다. 확정하면
        재계약율에 바로 반영됩니다. <strong>날짜 정정</strong>으로 확정한 기록은 갱신에서 빠집니다.
      </div>
      {total > rows.length && (
        <div
          style={{ fontSize: 11, color: 'var(--dday-warning)', marginBottom: 8 }}
          data-testid="renewal-review-truncated"
        >
          {total}건 중 최근 {limit}건만 보여 줍니다. 처리하면 다음 기록이 올라옵니다.
        </div>
      )}
      {failed && (
        <div
          style={{ fontSize: 12, color: 'var(--fg-danger)', marginBottom: 8 }}
          data-testid="renewal-review-error"
        >
          {failed}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r) => (
          <div key={r.id} data-testid="renewal-review-row" style={reviewRowBox}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <strong style={{ fontSize: 13 }}>{r.customerName || '(고객사 미확인)'}</strong>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{r.contractHumanId}</span>
              {canDecide && (
                <span style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                  <button
                    type="button"
                    disabled={pendingId === r.id}
                    onClick={() =>
                      decide.mutate({ id: r.id, decision: ContractRenewalReviewDecision.RENEWAL })
                    }
                    style={reviewDecideButton}
                  >
                    {CONTRACT_RENEWAL_REVIEW_DECISION_LABEL.RENEWAL}
                  </button>
                  <button
                    type="button"
                    disabled={pendingId === r.id}
                    onClick={() =>
                      decide.mutate({
                        id: r.id,
                        decision: ContractRenewalReviewDecision.CORRECTION,
                      })
                    }
                    style={reviewDecideButton}
                  >
                    {CONTRACT_RENEWAL_REVIEW_DECISION_LABEL.CORRECTION}
                  </button>
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              반납일 {r.previousEndDate ? kstYmd(r.previousEndDate) : '없음'} →{' '}
              <strong>{kstYmd(r.newEndDate)}</strong>
              {r.previousRentalTermMonths !== null && r.newRentalTermMonths !== null && (
                <span style={{ color: 'var(--fg-muted)' }}>
                  {' '}
                  · 기간 {r.previousRentalTermMonths}개월 → {r.newRentalTermMonths}개월
                </span>
              )}
              <span style={{ color: 'var(--fg-muted)' }}> · {kstYmd(r.renewedAt)} 저장</span>
            </div>
            {r.reviewNote && (
              <div style={{ fontSize: 11, color: 'var(--dday-warning)', marginTop: 4 }}>
                {r.reviewNote}
              </div>
            )}
            {!canDecide && (
              <div
                style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 6 }}
                data-testid="renewal-review-readonly"
              >
                확정은 사업팀 담당자만 할 수 있습니다.
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

/**
 * 확정 버튼을 보일 역할. **서버 정본(RENEWAL_REVIEW_DECIDE_ROLES)과 같은 집합**이어야
 * 한다 — 화면이 더 넓으면 눌러도 403 이 나고, 더 좁으면 권한 있는 사람이 일을 못 한다.
 * 실제 강제는 서버가 한다(이 함수는 헛클릭을 줄이는 것뿐).
 */
function canDecideRenewalReview(role: string | undefined): boolean {
  return role === UserRole.ADMIN || role === UserRole.MANAGER || role === UserRole.SALES_MANAGER;
}

const reviewRowBox: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  padding: '6px 10px',
  background: 'var(--bg-surface)',
};

/** 마감 임박 계약 · 확인이 필요한 갱신 기록 두 칸을 나란히 놓는 줄. 창이 좁으면 위아래로 쌓인다. */
const pairRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  alignItems: 'flex-start',
};
const pairCard: CSSProperties = { flex: '1 1 420px', minWidth: 0 };

const reviewDecideButton: CSSProperties = {
  fontSize: 12,
  padding: '2px 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  cursor: 'pointer',
};

/**
 * 분기 추이 막대. 모수 0 분기는 **막대를 그리지 않고** 회색 '–' 로 둔다 — 0% 와
 * 같은 모양으로 그리면 "그 분기에 아무도 갱신하지 않았다"는 거짓이 된다.
 * '확정 전' 분기는 막대를 흐리게 + 라벨에 별표.
 */
function RenewalRateQuarterBars({ quarters }: { quarters: RenewalRateBucket[] }) {
  if (quarters.length === 0) {
    return <div style={SHARED_EMPTY_HINT}>표시할 분기가 없습니다.</div>;
  }
  const BAR_AREA_PX = 72;
  return (
    <div
      data-testid="renewal-rate-quarter-bars"
      style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 110, paddingTop: 8 }}
    >
      {quarters.map((q) => {
        const heightPx = q.rate === null ? 0 : Math.max(q.rate * BAR_AREA_PX, q.rate > 0 ? 3 : 0);
        return (
          <div
            key={q.key}
            title={
              q.rate === null
                ? `${q.label}: 아직 만기 도래 계약이 없습니다`
                : `${q.label}: ${formatRatePct(q.rate)}, 만기 ${q.dueRobots}대 중 ${q.renewedRobots}대${q.provisional ? ', 확정 전' : ''}`
            }
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
            }}
          >
            <span
              style={{ fontSize: 10, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}
            >
              {q.rate === null ? '–' : `${Math.round(q.rate * 100)}`}
            </span>
            <div
              style={{
                width: '100%',
                height: `${heightPx}px`,
                background: 'var(--robot-operating)',
                opacity: q.provisional ? 0.45 : 1,
                borderRadius: '3px 3px 0 0',
                transition: 'height 0.4s ease-out',
              }}
            />
            <span style={{ fontSize: 9, color: 'var(--fg-subtle)' }}>
              {q.label.replace('년 ', '.')}
              {q.provisional ? '*' : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const provisionalBadge: CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--bg-warning-subtle)',
  color: 'var(--dday-warning)',
  fontWeight: 500,
};

const churnRangeButton: CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};

const churnRangeButtonActive: CSSProperties = {
  background: 'var(--bg-info-subtle)',
  // border 는 shorthand 로 통째 교체 — borderColor 만 얹으면 React 가
  // 재렌더 때 "shorthand/longhand 혼용" 경고를 낸다.
  border: '1px solid var(--fg-brand)',
  color: 'var(--fg-brand)',
  fontWeight: 600,
};
